/*
 * Pocket Pull Engine
 * ------------------
 * Drag-to-reveal logic for the pocket tee popup. Adapted from the inline script
 * in sections/pocket-pull.liquid, which is deliberately left untouched.
 *
 * Everything above the DOM controller is pure so it can be unit tested in Node.
 * Loads as CommonJS under `node --test`, and as window.PocketPullEngine in the
 * browser, where `module` is undefined.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  else { root.PocketPullEngine = api; }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  /* Drag delta to progress. Past the end we report the remainder separately so
     the caller can rubber-band it instead of clipping it. */
  function progressFromDrag(startProgress, deltaY, dragDistance) {
    var raw = startProgress + (deltaY / dragDistance);
    return {
      progress: clamp(raw, 0, 1),
      overpull: raw > 1 ? raw - 1 : 0
    };
  }

  /* Diminishing returns past the end, so it reads as fabric and not a slider. */
  function overpullOffset(over) {
    return over > 0 ? 26 * (1 - Math.exp(-over * 1.6)) : 0;
  }

  /* Hard cut, no crossfade. One frame visible at a time, like a flipbook. */
  function frameIndexFor(progress, frameCount) {
    if (frameCount <= 1) { return 0; }
    return Math.round(clamp(progress, 0, 1) * (frameCount - 1));
  }

  /* object-fit:cover crops whatever does not fit. Left alone the browser crops
     around the centre, which drifts the pocket off screen on wide viewports.
     These two work out where the focal point has to sit so the pocket stays put,
     and where the drag band ends up once the crop has eaten part of the frame.
     Ported from the layout() routine in sections/pocket-pull.liquid. */
  function coverCrop(boxW, boxH, frameW, frameH, focalX, focalY) {
    var scale = Math.max(boxW / frameW, boxH / frameH);
    var vFrac = clamp(boxH / (frameH * scale), 0, 1);
    var hFrac = clamp(boxW / (frameW * scale), 0, 1);
    return {
      x: hFrac >= 1 ? 0.5 : clamp((focalX - hFrac / 2) / (1 - hFrac), 0, 1),
      y: vFrac >= 1 ? 0.5 : clamp((focalY - vFrac / 2) / (1 - vFrac), 0, 1),
      vFrac: vFrac,
      hFrac: hFrac
    };
  }

  /* Map the image-space drag band into container space through that crop. */
  function hotspotBand(crop, hsTop, hsHeight) {
    var visTop = crop.vFrac >= 1 ? 0 : crop.y * (1 - crop.vFrac);
    var t = clamp((hsTop - visTop) / crop.vFrac, 0, 1);
    var b = clamp((hsTop + hsHeight - visTop) / crop.vFrac, 0, 1);
    return { top: t, height: Math.max(0, b - t) };
  }

  /* The photo is the popup's background, so it must always cover the box. Within that,
     the copy panel overlays the foot and hides whatever sits under it, so the part of the
     frame that matters (roiTop..subjectBottom: ears to the pocket opening) has to land in
     the clear area above the tallest panel. When there is more clear room than a cover
     crop needs, the frame is zoomed IN so the subject fills that room, capped so the
     pocket (subjectWidth of the frame) never runs off the sides. Never below cover.

     Returns pixel geometry for the frames wrapper (dispW/dispH/left/top) plus the same
     x/y/vFrac/hFrac contract coverCrop has, so hotspotBand keeps working. */
  function subjectCrop(o) {
    var coverScale = Math.max(o.boxW / o.frameW, o.boxH / o.frameH);
    var scale = coverScale;
    var subjH = (typeof o.roiTop === 'number' && typeof o.subjectBottom === 'number') ? o.subjectBottom - o.roiTop : 0;
    if (subjH > 0 && o.visibleH > 0 && o.subjectBottom < 1) {
      /* With D the displayed frame height, the band can start at roiTop - margin only once
         D >= boxH / (1 - roiTop + margin); below that the band is pinned to the frame's
         bottom and the subject slides DOWN as D shrinks, so a smaller zoom never helps.
         So the slice fits above the copy iff visibleH / (subjH + margin) clears that
         floor, and that quotient is then the largest D allowed. Otherwise: cover. */
      var m = typeof o.topMargin === 'number' ? o.topMargin : 0;
      var dFloor = o.boxH / (1 - o.roiTop + m);
      var dFit = o.visibleH / (subjH + m);
      if (dFit >= dFloor) {
        var cap = dFit / o.frameH;
        if (o.subjectWidth > 0) { cap = Math.min(cap, o.boxW / (o.frameW * o.subjectWidth)); }
        if (cap > coverScale) { scale = cap; }
      }
    }
    var dispW = o.frameW * scale, dispH = o.frameH * scale;
    var vFrac = clamp(o.boxH / dispH, 0, 1), hFrac = clamp(o.boxW / dispW, 0, 1);

    var visTop;
    if (vFrac >= 1) {
      visTop = 0;
    } else if (typeof o.roiTop === 'number') {
      var margin = typeof o.topMargin === 'number' ? o.topMargin : 0;
      var lo = 0, hi = 1 - vFrac;
      if (typeof o.roiBottom === 'number' && o.roiBottom - o.roiTop <= vFrac) {
        /* The whole pocket fits in the band: keep its bottom edge inside too. */
        lo = Math.max(0, o.roiBottom - vFrac);
      }
      if (hi < lo) { hi = lo; }
      visTop = clamp(o.roiTop - margin, lo, hi);
    } else {
      var fy = typeof o.focalY === 'number' ? o.focalY : 0.5;
      visTop = clamp(fy - vFrac / 2, 0, 1 - vFrac);
    }
    var fx = typeof o.focalX === 'number' ? o.focalX : 0.5;
    var visLeft = hFrac >= 1 ? 0 : clamp(fx - hFrac / 2, 0, 1 - hFrac);

    return {
      scale: scale, dispW: dispW, dispH: dispH,
      left: -visLeft * dispW, top: -visTop * dispH,
      x: hFrac >= 1 ? 0.5 : clamp(visLeft / (1 - hFrac), 0, 1),
      y: vFrac >= 1 ? 0.5 : clamp(visTop / (1 - vFrac), 0, 1),
      vFrac: vFrac, hFrac: hFrac
    };
  }

  /* One transition table, so an illegal jump is impossible by construction
     rather than by discipline. Unknown events are no-ops; unknown states throw,
     because that can only be a bug in the caller. */
  var TRANSITIONS = {
    idle:      { pullComplete: 'pulled', skip: 'capture', dismiss: 'dismissed' },
    pulled:    { advance: 'capture', dismiss: 'dismissed' },
    capture:   { submitSuccess: 'sms', submitDone: 'done', submitError: 'capture', dismiss: 'dismissed' },
    sms:       { smsSuccess: 'done', smsSkip: 'done', smsError: 'sms', dismiss: 'dismissed' },
    done:      { dismiss: 'dismissed' },
    dismissed: {}
  };

  var STATES = ['idle', 'pulled', 'capture', 'sms', 'done', 'dismissed'];

  function nextState(current, event) {
    var row = TRANSITIONS[current];
    if (!row) { throw new Error('unknown state: ' + current); }
    return Object.prototype.hasOwnProperty.call(row, event) ? row[event] : current;
  }

  /* Deliberately permissive. Klaviyo is the authority on deliverability; our job
     is only to catch the obvious typo before spending a round trip on it. */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isValidEmail(value) {
    if (typeof value !== 'string') { return false; }
    return EMAIL_RE.test(value.trim());
  }

  /* +1 is shared across the NANP, so a valid-looking number may be Canadian or
     Caribbean. This build is US only and the owner explicitly excluded Canada, and
     the account's toll-free IS approved for Canada, so an unfiltered number would
     really send under copy written for US law. US territories stay allowed. */
  var NON_US_NANP = {
    '204':1,'226':1,'236':1,'249':1,'250':1,'263':1,'289':1,'306':1,'343':1,'354':1,
    '365':1,'367':1,'368':1,'382':1,'387':1,'403':1,'416':1,'418':1,'428':1,'431':1,
    '437':1,'438':1,'450':1,'468':1,'474':1,'506':1,'514':1,'519':1,'548':1,'579':1,
    '581':1,'584':1,'587':1,'600':1,'604':1,'613':1,'622':1,'639':1,'647':1,'672':1,
    '683':1,'705':1,'709':1,'742':1,'753':1,'778':1,'780':1,'782':1,'807':1,'819':1,
    '825':1,'867':1,'873':1,'879':1,'902':1,'905':1,
    '242':1,'246':1,'264':1,'268':1,'284':1,'345':1,'441':1,'473':1,'649':1,'658':1,
    '664':1,'721':1,'758':1,'767':1,'784':1,'809':1,'829':1,'849':1,'868':1,'869':1,
    '876':1
  };

  /* US only, so we can be strict. NANP area codes and exchange codes never begin
     with 0 or 1, which cheaply rejects most typos and junk. */
  function toE164US(raw) {
    if (typeof raw !== 'string') { return null; }
    var d = raw.replace(/[^0-9]/g, '');
    if (d.length === 11) {
      if (d.charAt(0) !== '1') { return null; }
      d = d.slice(1);
    }
    if (d.length !== 10) { return null; }
    if (d.charAt(0) === '0' || d.charAt(0) === '1') { return null; }
    if (d.charAt(3) === '0' || d.charAt(3) === '1') { return null; }
    if (NON_US_NANP[d.slice(0, 3)]) { return null; }
    return '+1' + d;
  }

  function buildSubscribePayload(opts) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: String(opts.email).trim(),
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          },
          custom_source: opts.source
        },
        relationships: { list: { data: { type: 'list', id: opts.listId } } }
      }
    };
  }

  /* Mirrors buildSubscribePayload in shape. Carries the email too, which is what
     ties the SMS consent to the profile the email step already created. Only SMS
     consent is sent here; email consent was already sent by the email step and
     must not be resent. */
  function buildSmsPayload(opts) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: String(opts.email).trim(),
                phone_number: opts.phone,
                subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          },
          custom_source: opts.source
        },
        relationships: { list: { data: { type: 'list', id: opts.listId } } }
      }
    };
  }

  function subscribeUrl(companyId) {
    return 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
      encodeURIComponent(companyId);
  }

  /* 409 means the profile is already on the list. From the visitor's side that
     is a success: they are subscribed, and telling them otherwise would be a lie
     that costs us the capture.

     Only 422 is treated as a bad address. We already validate the address
     client-side before submitting, so a 400 that arrives has passed our own
     check and is far more likely a bad klaviyo_list_id setting than a bad
     address. Mapping 400 to 'invalid' would tell every visitor their email is
     wrong on a single typo in that setting, so it falls through to 'error'
     instead. */
  function classifyResponse(status) {
    if (status >= 200 && status < 300) { return 'success'; }
    if (status === 409) { return 'success'; }
    if (status === 422) { return 'invalid'; }
    if (status === 429) { return 'retry'; }
    return 'error';
  }

  /* Order matters: consent is checked first so the reported reason is the real
     blocker and not an incidental one. The reason string is what gets logged,
     so a misleading order would send someone debugging the wrong thing.
     marketingAllowed === null means the privacy API could not tell us; that is
     not a denial, and consent was already chosen, so we proceed. */
  function shouldShow(ctx) {
    /* Theme editor: the merchant is looking at it on purpose. Suppression, consent
       and account gates all describe shoppers, not the person configuring it. */
    if (ctx.designMode) { return { show: true, reason: 'design-mode' }; }
    /* Owner decision: the popup no longer waits for a cookie choice. Rendering a signup
       form is not tracking, and submitting it carries its own disclosure and its own
       explicit consent, independent of the cookie banner. An explicit DENIAL is still
       honoured: "has not chosen yet" and "said no" are different answers. */
    if (ctx.marketingAllowed === false) { return { show: false, reason: 'marketing-denied' }; }
    if (ctx.isAccountHolder && ctx.disableForAccountHolders) {
      return { show: false, reason: 'account-holder' };
    }
    if (ctx.suppressedUntil && ctx.now < ctx.suppressedUntil) {
      return { show: false, reason: 'suppressed' };
    }
    return { show: true, reason: 'ok' };
  }

  /* After a successful email capture: the SMS step is offered ONLY when its panel is
     in the DOM (US market at render time) AND the geo check has positively confirmed a
     US visitor. Pending, failed, or foreign all mean email-only. Strict equality on
     purpose: an unexpected status value must never unlock a TCPA disclosure. */
  function smsRoute(ctx) {
    return (ctx && ctx.smsPanel === true && ctx.usStatus === 'us') ? 'submitSuccess' : 'submitDone';
  }

  /* ---- Browser controller. Everything above this line is pure. ---- */

  var COOKIE = 'rnd_pocket_popup';
  var SESSION_KEY = 'rnd_pocket_popup_session';

  /* Three frequency modes:
       'days'    production. One impression per popup_days, via a cookie.
       'session' one impression per browser tab session, via sessionStorage.
                 Closing the tab resets it, so the popup can be exercised
                 repeatedly while testing without waiting out a 30 day cookie.
       'always'  every page load. Testing only, never ship this. */
  function readSuppressedUntil(doc, win, mode) {
    if (mode === 'always') { return 0; }
    if (mode === 'session') {
      /* Storage throws in some privacy modes. Fail OPEN: better to show the
         popup than to silently disable it because sessionStorage was blocked. */
      try { return win.sessionStorage.getItem(SESSION_KEY) ? Infinity : 0; }
      catch (e) { return 0; }
    }
    var m = (doc.cookie || '').match(new RegExp('(?:^|; )' + COOKIE + '=(\\d+)'));
    return m ? parseInt(m[1], 10) : 0;
  }

  /* Review escape hatch: ?popup=reset clears both suppression stores and forces this
     pageview through the gate. Consent is deliberately NOT bypassed, so this cannot be
     used to show a marketing popup to someone who declined cookies. */
  function resetRequested(win) {
    try { return /[?&]popup=reset\b/.test(win.location.search); } catch (e) { return false; }
  }

  function clearSuppression(doc, win) {
    try { doc.cookie = COOKIE + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'; } catch (e) {}
    try { win.sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  function suppress(doc, win, mode, days) {
    if (mode === 'always') { return; }
    if (mode === 'session') {
      try { win.sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
      return;
    }
    var until = Date.now() + days * 86400000;
    doc.cookie = COOKIE + '=' + until + ';path=/;max-age=' + (days * 86400);
  }

  /* Mirrors the consent detection already proven in snippets/klaviyo-form-fallback.liquid. */
  function readConsent(win) {
    var out = { consentChosen: false, marketingAllowed: null };
    try {
      var cp = win.Shopify && win.Shopify.customerPrivacy;
      if (cp) {
        if (cp.shouldShowBanner && cp.shouldShowBanner() === false) { out.consentChosen = true; }
        var fn = cp.marketingAllowed || cp.firstPartyMarketingAllowed;
        if (typeof fn === 'function') { out.marketingAllowed = fn.call(cp); }
      }
    } catch (e) {}
    return out;
  }

  function track(name, props) {
    try { if (window.posthog && window.posthog.capture) { window.posthog.capture(name, props || {}); } }
    catch (e) {}
  }

  function init(root) {
    if (!root || root.dataset.ppInit) { return; }
    root.dataset.ppInit = '1';

    var doc = root.ownerDocument;
    var win = doc.defaultView;

    var frames = [].slice.call(root.querySelectorAll('[data-frame]'));
    var hotspot = root.querySelector('[data-hotspot]');
    var wrap = root.querySelector('[data-frames]');
    if (!frames.length || !hotspot || !wrap) { return; }

    var dragDistance = parseInt(root.getAttribute('data-drag-distance'), 10) || 320;
    var days = parseInt(root.getAttribute('data-days'), 10) || 30;
    var daysDeclined = parseInt(root.getAttribute('data-days-declined'), 10) || 3;
    var freq = root.getAttribute('data-frequency') || 'days';
    var designMode = !!(win.Shopify && win.Shopify.designMode);

    /* Never write suppression from inside the theme editor: one close there would
       hide the popup for the rest of the editing session. */
    function remember(forDays) {
      if (designMode) { return; }
      suppress(doc, win, freq, forDays);
    }

    /* ?popup=reset in the URL: wipe any prior suppression and show regardless. Used for
       shared review links so a teammate always sees the popup on first load. */
    var forceShow = resetRequested(win);
    if (forceShow) { clearSuppression(doc, win); }
    /* 0 is a real value (show on load). `|| 5` treated it as missing and forced 5s. */
    var delayAttr = parseInt(root.getAttribute('data-delay'), 10);
    var delay = (isNaN(delayAttr) ? 5 : delayAttr) * 1000;

    var state = 'idle';
    var progress = 0;
    var shown = -1;
    var dragging = false;
    var startY = 0;
    var startProgress = 0;
    var lastFocus = null;
    /* Set when the email step succeeds. The SMS step needs this to tie SMS
       consent to the same profile, and the input it came from may be cleared
       or re-rendered by then, so it is captured here rather than re-read. */
    var capturedEmail = '';

    /* Damped spring back to rest, ported from sections/pocket-pull.liquid.
       An incomplete pull should not teleport back to frame 1: it should read
       as an invitation to try again, the same jiggle the pocket tee hero uses. */
    var velocity = 0;
    var pointerVel = 0;
    var rafId = null;
    var lastY = 0;
    var lastT = 0;
    var STIFFNESS = 0.18, DAMPING = 0.72;

    var stage = root.querySelector('[data-stage]');

    /* Without this the pocket drifts off-centre on wide viewports and the drag
       band stops lining up with what the visitor can actually see. */
    var panelBox = root.querySelector('.pocketpopup__panels');
    var lockedHeight = 0;
    var idlePanelH = 0;

    function layout() {
      if (!stage) { return; }
      var w = stage.clientWidth, h = stage.clientHeight;
      if (!w || !h) { return; }
      var num = function (name, dflt) {
        var v = parseInt(root.getAttribute(name), 10);
        return isNaN(v) ? dflt : v;
      };
      /* Clear room = box minus the IDLE copy (the state in which the visitor studies
         the pocket), plus half the dissolve, which is see-through enough for the subject
         to run into. Captured while idle and reused, so the crop is the same in every
         state and never jumps mid-flow; the later, taller panels simply cover more. */
      if (state === 'idle' && panelBox) { idlePanelH = panelBox.offsetHeight; }
      var panelH = idlePanelH || (panelBox ? panelBox.offsetHeight : 0);
      var visibleH = Math.max(0, h - panelH + num('data-fade-px', 0) * 0.5);
      var crop = subjectCrop({
        boxW: w, boxH: h, visibleH: visibleH,
        frameW: num('data-frame-w', 1080), frameH: num('data-frame-h', 1920),
        focalX: num('data-focal-x', 54) / 100, focalY: num('data-focal-y', 47) / 100,
        roiTop: num('data-roi-top', 30) / 100, roiBottom: num('data-roi-bottom', 97) / 100,
        subjectBottom: num('data-subject-bottom', 62) / 100,
        subjectWidth: num('data-subject-width', 80) / 100,
        topMargin: 0.02
      });
      /* Geometry goes on the wrapper, so the four frames stay plain 100% images inside it
         and the overpull translate on the same element still composes. */
      wrap.style.width = crop.dispW.toFixed(2) + 'px';
      wrap.style.height = crop.dispH.toFixed(2) + 'px';
      wrap.style.left = crop.left.toFixed(2) + 'px';
      wrap.style.top = crop.top.toFixed(2) + 'px';

      var band = hotspotBand(
        crop,
        num('data-hotspot-top', 34) / 100,
        num('data-hotspot-height', 58) / 100
      );
      hotspot.style.top = (band.top * 100).toFixed(2) + '%';
      hotspot.style.height = (band.height * 100).toFixed(2) + '%';
    }

    function relayout() { lockPanelHeight(); layout(); }
    win.addEventListener('resize', relayout);
    win.addEventListener('orientationchange', relayout);

    function panels() { return [].slice.call(root.querySelectorAll('[data-panel]')); }


    /* The ticket must not move between the states that SHOW it, so those are pinned to
       their tallest. Idle is deliberately excluded: it shows no ticket and needs far less
       room, and forcing it to the SMS panel's height was donating ~150px of photo to a
       panel that had nothing to put there. Idle therefore sizes to its own content and
       the stage takes the rest. */

    function measurePanelLock() {
      if (!panelBox) { return; }
      var prev = panelBox.style.minHeight;
      panelBox.style.minHeight = '';
      var all = panels();
      if (!all.length) { panelBox.style.minHeight = prev; return; }
      var active = null;
      all.forEach(function (pnl) { if (!pnl.hidden) { active = pnl; } });
      /* The ticket row is display:none in idle; every locked state shows it, so measure with it. */
      var reveal = root.querySelector('[data-reveal]');
      var revealWasHidden = reveal ? reveal.hidden : false;
      if (reveal) { reveal.hidden = false; }

      lockedHeight = 0;
      all.forEach(function (pnl) {
        if (pnl.getAttribute('data-panel') === 'idle') { return; }
        all.forEach(function (other) { other.hidden = (other !== pnl); });
        if (panelBox.offsetHeight > lockedHeight) { lockedHeight = panelBox.offsetHeight; }
      });
      all.forEach(function (other) { other.hidden = (other !== active); });
      if (reveal) { reveal.hidden = revealWasHidden; }
      applyPanelLock();
    }

    /* Idle floats free; every other state sits at the pinned height. */
    function applyPanelLock() {
      if (!panelBox) { return; }
      panelBox.style.minHeight = (state === 'idle' || !lockedHeight) ? '' : (lockedHeight + 'px');
    }

    function lockPanelHeight() { measurePanelLock(); }

    function render() {
      panels().forEach(function (p) { p.hidden = p.getAttribute('data-panel') !== state; });
      applyPanelLock();
      layout();
      var reveal = root.querySelector('[data-reveal]');
      if (reveal) { reveal.hidden = (state === 'idle'); }
    }

    function go(event) {
      var next = nextState(state, event);
      if (next === state) { return; }
      state = next;
      render();
      if (state === 'pulled') {
        track('pocket_popup_pull_completed');
        win.setTimeout(function () { go('advance'); }, 1100);
      }
      if (state === 'capture') {
        var input = root.querySelector('[data-email]');
        if (input) { input.focus(); }
      }
      if (state === 'sms') {
        /* The email is already banked. Write the suppression cookie now, not just
           on done/close, so a visitor who submits the email then clicks away
           while the SMS step is open is not re-asked for it on the next
           pageview. Same cookie name and path as done/close, so this is just
           an idempotent overwrite there. */
        remember(days);
        track('pocket_popup_sms_shown');
        var phoneInput = root.querySelector('[data-phone]');
        if (phoneInput) { phoneInput.focus(); }
      }
      if (state === 'done') {
        remember(days);
        /* The submit button that had focus is about to be display:none'd by
           render(), which would drop focus to <body>, outside the trap.
           Move it to the done heading instead so focus stays inside the
           modal and the success message gets announced. */
        var dh = root.querySelector('[data-done-heading]');
        if (dh) { dh.focus(); }
        win.setTimeout(function () { if (state === 'done') { close(); } }, 4000);
      }
      if (state === 'dismissed') { close(); }
    }

    function paint(p) {
      progress = clamp(p, 0, 1);
      var idx = frameIndexFor(progress, frames.length);
      if (idx !== shown) {
        for (var k = 0; k < frames.length; k++) { frames[k].style.opacity = (k === idx) ? '1' : '0'; }
        shown = idx;
      }
      hotspot.setAttribute('aria-valuenow', Math.round(progress * 100));
      /* A fast flick can carry the spring past the release point all the way to
         progress 1. That is allowed by design: an energetic pull legitimately
         completes the reveal, same as reaching 1 by drag or the End key.
         step() bails as soon as state leaves 'idle' (see springBack), so the
         flipbook cannot rewind underneath the reveal once this latches. */
      if (progress >= 1 && state === 'idle') { go('pullComplete'); }
    }

    function stopAnim() {
      if (rafId) { win.cancelAnimationFrame(rafId); rafId = null; }
    }

    /* Bouncing off zero is what gives the pocket its jiggle when it snaps shut. */
    function springBack() {
      stopAnim();

      var reduce = win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) { paint(0); return; }

      function step() {
        if (state !== 'idle' || dragging) { rafId = null; return; }
        velocity = (velocity + (-progress * STIFFNESS)) * DAMPING;
        var next = progress + velocity;
        if (next < 0) { next = 0; velocity = -velocity * 0.35; }
        paint(next);

        if (Math.abs(next) < 0.0015 && Math.abs(velocity) < 0.0015) {
          paint(0);
          velocity = 0;
          rafId = null;
          return;
        }
        rafId = win.requestAnimationFrame(step);
      }
      rafId = win.requestAnimationFrame(step);
    }

    hotspot.addEventListener('pointerdown', function (e) {
      if (state !== 'idle') { return; }
      if (e.button !== undefined && e.button !== 0) { return; }
      stopAnim();
      dragging = true;
      startY = e.clientY;
      startProgress = progress;
      lastY = e.clientY;
      lastT = e.timeStamp;
      pointerVel = 0;
      hotspot.classList.add('is-dragging');
      if (progress === 0) { track('pocket_popup_pull_started'); }
      try { hotspot.setPointerCapture(e.pointerId); } catch (err) {}
    });

    hotspot.addEventListener('pointermove', function (e) {
      if (!dragging || state !== 'idle') { return; }
      e.preventDefault();

      var dt = e.timeStamp - lastT;
      if (dt > 0) {
        // progress units per frame, used as the spring's launch velocity
        pointerVel = ((e.clientY - lastY) / dragDistance) * (16 / dt);
        lastY = e.clientY;
        lastT = e.timeStamp;
      }

      var r = progressFromDrag(startProgress, e.clientY - startY, dragDistance);
      wrap.style.transform = r.overpull ? 'translateY(' + overpullOffset(r.overpull).toFixed(1) + 'px)' : '';
      paint(r.progress);
    });

    function release() {
      if (!dragging) { return; }
      dragging = false;
      hotspot.classList.remove('is-dragging');
      wrap.style.transform = '';
      if (state === 'idle') { velocity = pointerVel; springBack(); }
    }
    hotspot.addEventListener('pointerup', release);
    hotspot.addEventListener('pointercancel', release);
    hotspot.addEventListener('lostpointercapture', release);

    hotspot.addEventListener('keydown', function (e) {
      stopAnim();
      if (state !== 'idle') { return; }
      var step = 0.12;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); paint(progress + step); }
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); paint(progress - step); }
      else if (e.key === 'End') { e.preventDefault(); paint(1); }
      else if (e.key === 'Home') { e.preventDefault(); paint(0); }
    });

    var skip = root.querySelector('[data-skip]');
    if (skip) { skip.addEventListener('click', function () { track('pocket_popup_skipped'); go('skip'); }); }

    /* A subscriber has no reason to see this again for a month. Someone who dismissed
       it has only told us "not now", so a month of silence throws away every later
       chance to convert them. capturedEmail is the honest signal: it is set only by a
       successful email submit. */
    var closing = false;

    function close() {
      if (closing || root.hidden) { return; }
      closing = true;
      /* Bank the suppression immediately. If the visitor navigates mid-fade the cookie is
         already written, so they are not asked again on the next page. */
      remember(capturedEmail ? days : daysDeclined);
      root.classList.remove('is-open');

      function finish() {
        root.hidden = true;
        /* Hand the screen back to the cookie banner now, not before. */
        try {
          doc.documentElement.classList.remove('pp-popup-open');
          releaseConsentBanner();
        } catch (e) {}
        /* Scroll lock released only once it is actually gone, so the page behind cannot
           scroll underneath a still-visible overlay. */
        doc.documentElement.style.overflow = '';
        if (lastFocus && lastFocus.focus) { lastFocus.focus(); }
        closing = false;
      }
      if (prefersReducedMotion() || fadeMs <= 0) { finish(); return; }
      win.setTimeout(finish, fadeMs + 20);
    }

    function dismiss() {
      if (state === 'dismissed') { return; }
      track('pocket_popup_dismissed', { from: state });
      go('dismiss');
    }

    var closeBtn = root.querySelector('[data-close]');
    if (closeBtn) { closeBtn.addEventListener('click', dismiss); }
    var overlay = root.querySelector('[data-overlay]');
    if (overlay) { overlay.addEventListener('click', dismiss); }
    doc.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) { dismiss(); }
    });

    /* Keyboard modality. The drag area is focused by script on open, and browsers
       treat that (and a pointer drag on a focused element) as keyboard focus, painting
       a ring across the photo. So the ring is only shown once a navigation key has
       actually been used, and any pointer contact switches it back off. Escape is
       excluded on purpose: closing is not navigating. */
    var KBD_KEYS = { Tab: 1, ArrowUp: 1, ArrowDown: 1, ArrowLeft: 1, ArrowRight: 1, Home: 1, End: 1, ' ': 1, Enter: 1 };
    root.addEventListener('keydown', function (e) {
      if (KBD_KEYS[e.key]) { root.classList.add('is-kbd'); }
    });
    root.addEventListener('pointerdown', function () { root.classList.remove('is-kbd'); });

    /* Focus trap. A modal that lets focus wander behind it is not a modal. */
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') { return; }
      var f = [].slice.call(root.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) { return; }
      if (f.indexOf(doc.activeElement) === -1) { e.preventDefault(); f[0].focus(); return; }
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    wireForm(root, win, track, go, function (email) { capturedEmail = email; }, function () { return usStatus; });
    wireSmsForm(root, win, track, go, function () { return capturedEmail; });

    /* Only open once every frame has decoded, so nobody sees a broken flipbook. */
    var fadeMs = parseInt(root.getAttribute('data-fade'), 10);
    if (isNaN(fadeMs)) { fadeMs = 260; }

    function prefersReducedMotion() {
      try { return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); }
      catch (e) { return false; }
    }

    /* The banner outranks us on z-index (2147483647), so while it is up it would bury the
       form. We no longer wait for it, so shift our content clear of it instead and release
       the offset once it goes. */
    function offsetForConsentBanner() {
      var host = doc.querySelector('pandectes-cmp');
      var h = 0;
      try {
        if (host && host.shadowRoot) {
          var nodes = host.shadowRoot.querySelectorAll('div');
          for (var i = 0; i < nodes.length; i++) {
            var cs = win.getComputedStyle(nodes[i]);
            if (cs.display === 'none' || cs.visibility === 'hidden') { continue; }
            var r = nodes[i].getBoundingClientRect();
            if (r.height > 60 && r.width > 200 && r.bottom > 0 && r.top < win.innerHeight) {
              if (r.height > h) { h = r.height; }
            }
          }
        }
      } catch (e) {}
      root.style.paddingBottom = h ? h + 'px' : '';
      if (h !== lastBannerOffset) { lastBannerOffset = h; layout(); lockPanelHeight(); }
    }
    var lastBannerOffset = -1;

    /* Pandectes writes `display: block !important` and its z-index as INLINE styles on
       <pandectes-cmp>. An inline !important beats any stylesheet rule, so no amount of
       CSS can suppress it. The only way to sequence the two is to overwrite the inline
       property directly and put the original back afterwards. It also re-asserts itself,
       so hold it down for as long as we are open. */
    var cmpSavedStyle = null;
    var cmpHold = null;

    function suppressConsentBanner() {
      var host = doc.querySelector('pandectes-cmp');
      if (!host) { return; }
      if (cmpSavedStyle === null) { cmpSavedStyle = host.getAttribute('style') || ''; }
      if (host.style.display !== 'none') {
        host.style.setProperty('display', 'none', 'important');
      }
    }

    function releaseConsentBanner() {
      if (cmpHold) { win.clearInterval(cmpHold); cmpHold = null; }
      var host = doc.querySelector('pandectes-cmp');
      if (!host) { cmpSavedStyle = null; return; }
      /* Restore exactly what Pandectes had, rather than guessing at a value. */
      if (cmpSavedStyle !== null) { host.setAttribute('style', cmpSavedStyle); }
      else { host.style.removeProperty('display'); }
      cmpSavedStyle = null;
    }

    function open() {
      lastFocus = doc.activeElement;
      root.hidden = false;
      /* Suppress the cookie banner for as long as we are up, so the visitor answers one
         thing at a time. Released in close(), which is the only exit path. */
      try {
        doc.documentElement.classList.add('pp-popup-open');
        suppressConsentBanner();
        cmpHold = win.setInterval(suppressConsentBanner, 300);
      } catch (e) {}
      doc.documentElement.style.overflow = 'hidden';
      lockPanelHeight();   /* first: on mobile the stage takes the space the panels leave */
      layout();            /* then crop, against the stage size the lock just settled */
      render();
      /* The element was display:none a moment ago, so the from-state has not been
         committed and the transition would be skipped. Reading offsetHeight forces a
         synchronous reflow that commits it, then the class change animates.
         Deliberately NOT requestAnimationFrame: rAF does not fire in a background tab,
         and a visitor opening the site in one would have had the popup unhide with
         is-open never applied, leaving a fully transparent full-viewport overlay that
         swallowed every click. */
      void root.offsetHeight;
      root.classList.add('is-open');
      hotspot.focus();
      track('pocket_popup_shown');
    }

    function eligible() {
      var c = readConsent(win);
      return shouldShow({
        consentChosen: c.consentChosen,
        marketingAllowed: c.marketingAllowed,
        designMode: designMode,
        isAccountHolder: root.getAttribute('data-logged-in') === 'true',
        disableForAccountHolders: root.getAttribute('data-disable-accounts') === 'true',
        suppressedUntil: forceShow ? 0 : readSuppressedUntil(doc, win, freq),
        now: Date.now()
      });
    }

    /* Load through DETACHED Image objects, not the real <img>s. The real ones live inside
       a display:none subtree until we open, and iOS Safari defers loading there, so the
       gate could wait indefinitely and the popup only appeared once a scroll forced the
       browser to do the work. Detached images load immediately regardless.

       Also capped. A slow or dead image must never be able to stop the popup opening;
       worst case the first frame paints a moment late, which is far better than never. */
    var PRELOAD_CAP_MS = 1500;

    function promoteFrameSrcs() {
      frames.forEach(function (f) {
        var ds = f.getAttribute('data-src');
        if (ds && !f.getAttribute('src')) { f.setAttribute('src', ds); }
      });
    }

    function preloadThenOpen() {
      var pending = frames.map(function (f) {
        var ds = f.getAttribute('data-src') || f.getAttribute('src');
        if (!ds) { return Promise.resolve(); }
        return new Promise(function (res) {
          var pre = new win.Image();
          pre.onload = res;
          pre.onerror = res;
          pre.src = ds;
        });
      });
      var capped = Promise.race([
        Promise.all(pending),
        new Promise(function (res) { win.setTimeout(res, PRELOAD_CAP_MS); })
      ]);
      capped.then(function () { promoteFrameSrcs(); open(); });
    }

    var delayElapsed = false;
    var opened = false;

    /* Shopify's customerPrivacy.shouldShowBanner() reports false on this store even while
       the Pandectes banner is on screen, so the API alone is not a safe signal. The banner
       is a <pandectes-cmp> host whose content lives in a shadow root at z-index
       2147483647, above this popup, and it covers the whole panel area including the CTA.
       Look at the banner itself: if it is up, the visitor has not chosen yet, so wait. */
    function consentBannerShowing() {
      try {
        var host = doc.querySelector('pandectes-cmp');
        if (!host || !host.shadowRoot) { return false; }
        var nodes = host.shadowRoot.querySelectorAll('div');
        for (var i = 0; i < nodes.length; i++) {
          var cs = win.getComputedStyle(nodes[i]);
          if (cs.display === 'none' || cs.visibility === 'hidden') { continue; }
          var r = nodes[i].getBoundingClientRect();
          if (r.height > 60 && r.width > 200) { return true; }
        }
        return false;
      } catch (e) { return false; }
    }

    /* ?popup=debug paints a small panel saying exactly why the popup is or is not
       showing. Built because remote testers could only report "it does not work", which
       is not diagnosable: every plausible cause (consent, suppression, geo, stale asset,
       wrong theme) looks identical from the outside. */
    function debugRequested() {
      try {
        if (!/[?&]popup=debug\b/.test(win.location.search)) { return false; }
        /* Never on the live storefront, even with the param. This panel is a QA aid, not
           something a customer should ever be able to summon onto the real site. */
        var role = win.Shopify && win.Shopify.theme && win.Shopify.theme.role;
        return role !== 'main';
      } catch (e) { return false; }
    }

    function paintDebug() {
      var box = doc.getElementById('pp-debug');
      if (!box) {
        box = doc.createElement('div');
        box.id = 'pp-debug';
        box.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:2147483646;' +
          'background:#000;color:#0f0;font:11px/1.45 ui-monospace,Menlo,monospace;padding:10px;' +
          'border:1px solid #0f0;border-radius:6px;white-space:pre-wrap;max-height:60vh;overflow:auto';
        doc.body.appendChild(box);
      }
      var c = readConsent(win);
      var e = eligible();
      var sup = readSuppressedUntil(doc, win, freq);
      var lines = [
        'POCKET POPUP DEBUG',
        'build      : sms+fade+consent-gate',
        'theme      : ' + ((win.Shopify && win.Shopify.theme) ? (win.Shopify.theme.id + ' ' + win.Shopify.theme.role) : 'unknown'),
        'url        : ' + win.location.pathname + win.location.search.slice(0, 60),
        '',
        'popup open : ' + (!root.hidden),
        'state      : ' + state,
        'delay      : ' + delay + 'ms, elapsed=' + delayElapsed,
        '',
        'consent bnr: ' + consentBannerShowing() + '  <- true means it is WAITING on you',
        'shopify cp : chosen=' + c.consentChosen + ' marketing=' + c.marketingAllowed,
        'freq mode  : ' + freq,
        'suppressed : ' + (sup ? new Date(sup).toISOString().slice(0, 16) : 'no'),
        'eligible   : show=' + e.show + ' reason=' + e.reason,
        '',
        'sms panel  : ' + (!!root.querySelector('[data-panel="sms"]')) + '  us=' + usStatus + '  (offered only when both are true/us)',
        'frames     : ' + frames.length + ' loaded=' + frames.filter(function (f) { return f.getAttribute('src'); }).length,
        'viewport   : ' + win.innerWidth + 'x' + win.innerHeight
      ];
      box.textContent = lines.join('\n');
    }

    function tryOpen() {
      if (opened || !delayElapsed) { return; }
      if (!eligible().show) { return; }
      opened = true;
      preloadThenOpen();
    }

    /* The banner dismisses on interaction rather than firing an event we can subscribe to,
       so poll for it clearing. Bounded, and stops as soon as the popup opens. */
    var bannerWatch = null;
    var bannerTicks = 0;
    function watchForConsentChoice() {
      if (bannerWatch) { return; }
      bannerWatch = win.setInterval(function () {
        bannerTicks++;
        if (bannerTicks > 240) {        /* 240 x 500ms = 2 minutes */
          win.clearInterval(bannerWatch); bannerWatch = null; return;
        }
        tryOpen();
      }, 500);
    }

    /* window.Shopify may not exist yet. loadFeatures both guarantees
       customerPrivacy and gives us a second chance to evaluate. */
    try {
      if (win.Shopify && win.Shopify.loadFeatures) {
        win.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function () { tryOpen(); }
        );
      }
    } catch (e) {}

    /* Someone who ignores the banner at t=delay must still get the popup when
       they later choose. Deciding once would lose them permanently. */
    doc.addEventListener('visitorConsentCollected', tryOpen);
    win.addEventListener('PandectesEvent_OnConsent', tryOpen);

    /* The Liquid guard keyed on localization.country, which is the shopper's SELECTED
       market, not where they are. A first-time international visitor has selected
       nothing, so that guard lets them through. Confirm against the country Shopify
       actually detected, the same endpoint snippets/auto-country-switch.liquid uses.
       Fails CLOSED: if we cannot confirm the visitor is in the US we drop the SMS step,
       because showing a US-only TCPA disclosure to someone abroad is worse than losing
       an opt-in. */
    /* 'pending' until Shopify answers. The router treats pending exactly like foreign,
       so a slow or hanging lookup can never leak the SMS step: it simply is not offered
       until the answer is in, and only ever if that answer is US. */
    var usStatus = 'pending';
    function setUsStatus(v) { usStatus = v; root.setAttribute('data-us', v); }
    setUsStatus('pending');

    function confirmUsOrDropSms() {
      var panel = root.querySelector('[data-panel="sms"]');
      if (!panel) { setUsStatus('other'); return; }
      function drop() {
        setUsStatus('other');
        /* Never yank the panel out from under a visitor already on it; the router
           cannot have put them there without a positive answer, so this is only a
           safety net for a late, contradictory response. */
        if (state !== 'sms' && panel.parentNode) { panel.parentNode.removeChild(panel); }
      }
      var req;
      try {
        req = win.fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' });
      } catch (e) { drop(); return; }
      req.then(function (res) { return res.json(); })
         .then(function (data) {
           var detected = data && data.detected_values && data.detected_values.country
             && data.detected_values.country.handle;
           /* snippets/auto-country-switch.liquid:13 uppercases before comparing, so the
              handle's case is not guaranteed. Match that. This check fails closed, so a
              case mismatch would silently kill the SMS step for everyone, not just abroad. */
           if (String(detected || '').toUpperCase() === 'US') { setUsStatus('us'); }
           else { drop(); }
         })
         .catch(drop);
    }
    confirmUsOrDropSms();

    win.setTimeout(function () {
      delayElapsed = true;
      tryOpen();
      if (!opened) { watchForConsentChoice(); }
    }, delay);

    /* Theme editor: selecting the section in the sidebar (re)opens it from the top,
       deselecting closes it, like every other popup section in the customizer. The
       state machine has no way back from 'dismissed', so the editor path resets it
       directly rather than going through go(). */
    if (designMode) {
      function ownSection(e) {
        return !!(e && e.target && e.target.contains && e.target.contains(root));
      }
      function reopenForEditor() {
        stopAnim();
        root.classList.remove('is-kbd');
        state = 'idle'; progress = 0; shown = -1; capturedEmail = ''; closing = false;
        paint(0);
        render();
        if (root.hidden) { promoteFrameSrcs(); open(); }
      }
      doc.addEventListener('shopify:section:select', function (e) {
        if (ownSection(e)) { reopenForEditor(); }
      });
      doc.addEventListener('shopify:section:deselect', function (e) {
        if (ownSection(e) && !root.hidden) { close(); }
      });
    }

    if (debugRequested()) {
      win.setInterval(paintDebug, 700);
      paintDebug();
    }
  }

  function wireForm(root, win, trackFn, go, onSuccess, getUsStatus) {
    var form = root.querySelector('[data-form]');
    if (!form) { return; }
    var input = root.querySelector('[data-email]');
    var button = root.querySelector('[data-submit]');
    var errEl = root.querySelector('[data-error]');
    var busy = false;

    function showError(msg) {
      /* A live region populated while hidden is announced inconsistently.
         Unhide first, then set the text, so the reveal itself is the trigger. */
      errEl.hidden = false;
      errEl.textContent = msg;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) { return; }
      errEl.hidden = true;

      var email = input.value;
      if (!isValidEmail(email)) {
        showError(root.getAttribute('data-error-email') || "That email doesn't look right.");
        input.focus();
        return;
      }

      busy = true;
      button.disabled = true;

      /* win.fetch is dereferenced bare here. A synchronous throw (or a missing
         fetch) would otherwise escape this handler entirely, leaving busy=true
         and the CLAIM button disabled forever with no error shown. Route a
         synchronous failure through the same rejection path as a network one. */
      var p;
      try {
        p = win.fetch(subscribeUrl(root.getAttribute('data-company-id')), {
          method: 'POST',
          headers: { 'content-type': 'application/json', revision: '2026-07-15' },
          body: JSON.stringify(buildSubscribePayload({
            email: email,
            listId: root.getAttribute('data-list-id'),
            source: root.getAttribute('data-source')
          }))
        });
      } catch (err) {
        p = Promise.reject(err);
      }

      p.then(function (res) {
        return classifyResponse(res.status);
      }).catch(function () {
        return 'error';
      }).then(function (verdict) {
        busy = false;
        button.disabled = false;
        if (verdict === 'success') {
          if (typeof onSuccess === 'function') { onSuccess(email); }
          trackFn('pocket_popup_email_submitted');
          go(smsRoute({
            smsPanel: !!root.querySelector('[data-panel="sms"]'),
            usStatus: typeof getUsStatus === 'function' ? getUsStatus() : 'other'
          }));
          return;
        }
        /* The field is never cleared on failure. Making someone retype their
           address after our error is how you lose the capture. */
        trackFn('pocket_popup_submit_failed', { verdict: verdict });
        showError(verdict === 'invalid'
          ? (root.getAttribute('data-error-email') || "That email doesn't look right.")
          : (root.getAttribute('data-error-generic') || 'Something went wrong. Try again.'));
        input.focus();
      });
    });
  }

  /* SMS step. Only wired for US visitors, where the panel exists in the DOM at
     all; the guard on `panel` below is the same [data-panel="sms"] element the
     router keys on, so this is a safe no-op everywhere else, including once
     confirmUsOrDropSms() removes the panel for a visitor Shopify detected
     outside the US. getEmail reads the email already captured by wireForm's
     onSuccess, never the input, which may have been cleared or re-rendered by
     then. */
  function wireSmsForm(root, win, trackFn, go, getEmail) {
    var panel = root.querySelector('[data-panel="sms"]');
    if (!panel) { return; }
    var form = panel.querySelector('[data-sms-form]');
    if (!form) { return; }
    var input = root.querySelector('[data-phone]');
    var button = root.querySelector('[data-sms-submit]');
    var errEl = root.querySelector('[data-sms-error]');
    var busy = false;

    function showError(msg) {
      /* A live region populated while hidden is announced inconsistently.
         Unhide first, then set the text, so the reveal itself is the trigger. */
      errEl.hidden = false;
      errEl.textContent = msg;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) { return; }
      errEl.hidden = true;

      var phone = toE164US(input.value);
      if (!phone) {
        showError(root.getAttribute('data-error-phone') || "That number doesn't look right. US numbers only.");
        input.focus();
        return;
      }

      busy = true;
      button.disabled = true;

      /* Same synchronous-throw guard as the email form: win.fetch is
         dereferenced bare, so a synchronous throw (or a missing fetch) is
         routed through the same rejection path as a network failure instead
         of escaping the handler with busy stuck true. */
      var p;
      try {
        p = win.fetch(subscribeUrl(root.getAttribute('data-company-id')), {
          method: 'POST',
          headers: { 'content-type': 'application/json', revision: '2026-07-15' },
          body: JSON.stringify(buildSmsPayload({
            email: getEmail(),
            phone: phone,
            listId: root.getAttribute('data-list-id'),
            source: root.getAttribute('data-source') + ' SMS'
          }))
        });
      } catch (err) {
        p = Promise.reject(err);
      }

      p.then(function (res) {
        return classifyResponse(res.status);
      }).catch(function () {
        return 'error';
      }).then(function (verdict) {
        busy = false;
        button.disabled = false;
        if (verdict === 'success') {
          trackFn('pocket_popup_sms_submitted');
          go('smsSuccess');
          return;
        }
        /* The field is never cleared on failure. Making someone retype their
           number after our error is how you lose the capture. */
        trackFn('pocket_popup_sms_failed', { verdict: verdict });
        showError(verdict === 'invalid'
          ? (root.getAttribute('data-error-phone') || "That number doesn't look right. US numbers only.")
          : (root.getAttribute('data-error-generic') || 'Something went wrong. Try again.'));
        input.focus();
      });
    });

    var skip = root.querySelector('[data-sms-skip]');
    if (skip) {
      skip.addEventListener('click', function () {
        /* Same busy flag the submit handler sets. Without this, tapping skip
           while the POST from TEXT ME is in flight fires sms_skipped and then
           sms_submitted for one visitor. */
        if (busy) { return; }
        trackFn('pocket_popup_sms_skipped');
        go('smsSkip');
      });
    }
  }

  function boot() {
    if (typeof document === 'undefined') { return; }
    [].slice.call(document.querySelectorAll('[data-pocket-popup]')).forEach(init);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
    document.addEventListener('shopify:section:load', boot);
  }

  return {
    clamp: clamp,
    progressFromDrag: progressFromDrag,
    overpullOffset: overpullOffset,
    frameIndexFor: frameIndexFor,
    coverCrop: coverCrop,
    subjectCrop: subjectCrop,
    hotspotBand: hotspotBand,
    STATES: STATES,
    nextState: nextState,
    isValidEmail: isValidEmail,
    toE164US: toE164US,
    buildSubscribePayload: buildSubscribePayload,
    buildSmsPayload: buildSmsPayload,
    subscribeUrl: subscribeUrl,
    classifyResponse: classifyResponse,
    shouldShow: shouldShow,
    smsRoute: smsRoute,
    init: init
  };
});
